import {
  type GenerateTask,
  type OutputSizeTier,
  SIZE_TIER_CREDIT_MULTIPLIER,
} from '@studio/contract';

/**
 * Per-mode credit math. Mirrors the legacy `getPerUnitGenerationCost`
 * + `getCreditsSkin` + `handleFitToBudget` triad documented in
 * `docs/research/legacy-photo-studio-architecture.md`.
 *
 * Standard / Large / XL = 1× / 2× / 3× of the base credit.
 *   - Enhance / Edit: total = sizeTier × batch.
 *   - Match Style: per matched image = sizeTier; total = sizeTier × matchLookCount.
 *   - Match Scene: per matched image = sizeTier × 2; total = sizeTier × 2 × matchLookCount.
 *   - Merge: stepped on input count (2 / 3-4 / 5-6 / 7-8 → 1× / 1.5× / 2× / 2.5×),
 *           multiplied by batch.
 */

export interface CostInputs {
  task: GenerateTask;
  sizeTier: OutputSizeTier;
  batchSize: number;
  /** Match Look only — count of products being matched against the template. */
  matchLookCount?: number;
  /** Merge only — count of input images. */
  mergeImageCount?: number;
  /** Match Look sub-mode (style or scene) — Scene doubles the base. */
  matchLookSubMode?: 'style' | 'scene';
}

export const OUT_OF_CREDITS_THRESHOLD = 1;

const MERGE_INPUT_MULTIPLIER: Array<{ minInputs: number; mult: number }> = [
  { minInputs: 7, mult: 2.5 },
  { minInputs: 5, mult: 2 },
  { minInputs: 3, mult: 1.5 },
  { minInputs: 2, mult: 1 },
];

function mergeMultiplier(imageCount: number): number {
  for (const tier of MERGE_INPUT_MULTIPLIER) {
    if (imageCount >= tier.minInputs) return tier.mult;
  }
  return 1;
}

export function getPerUnitCost(input: CostInputs): number {
  const baseTier = SIZE_TIER_CREDIT_MULTIPLIER[input.sizeTier];
  switch (input.task) {
    case 'enhance':
    case 'edit':
      return baseTier;
    case 'match-style':
      return input.matchLookSubMode === 'scene' ? baseTier * 2 : baseTier;
    case 'merge':
      return baseTier * mergeMultiplier(input.mergeImageCount ?? 2);
  }
}

export function getTotalCost(input: CostInputs): number {
  const perUnit = getPerUnitCost(input);
  switch (input.task) {
    case 'enhance':
    case 'edit':
      return perUnit * input.batchSize;
    case 'match-style':
      return perUnit * (input.matchLookCount ?? 1);
    case 'merge':
      return perUnit * input.batchSize;
  }
}

export type CreditsSkin =
  | 'urgent'
  | 'danger'
  | 'warning'
  | 'warningLight'
  | 'standard'
  | 'neutralStandard';

export interface SkinInputs {
  totalCredits: number;
  availableCredits: number;
  hasMismatch?: boolean;
  sizeTier: OutputSizeTier;
}

/**
 * Pick the credits-chip skin per the legacy priority order:
 * urgent > danger > warning > warningLight > standard > neutralStandard.
 */
export function getCreditsSkin({
  totalCredits,
  availableCredits,
  hasMismatch = false,
  sizeTier,
}: SkinInputs): CreditsSkin {
  if (availableCredits <= OUT_OF_CREDITS_THRESHOLD) return 'urgent';
  if (totalCredits > availableCredits) return 'danger';
  if (hasMismatch) return 'warning';
  if (sizeTier !== 'standard') return 'warningLight';
  if (totalCredits > 2) return 'standard';
  return 'neutralStandard';
}

/**
 * Walk [currentTier, 'large', 'standard'] and pick the first tier
 * where `floor(available / perUnit) >= minRequiredCount`. Then clamp
 * batch (or matchLookCount on Match Style) to `min(affordable, max)`.
 *
 * Returns the same shape with possibly-different sizeTier + batchSize.
 * For Match Style the batchSize stays at 1; the matchLookCount is what
 * gets clamped (caller is responsible for that since we don't own
 * selection state here).
 */
export interface FitToBudgetResult {
  sizeTier: OutputSizeTier;
  batchSize: number;
  /** True if no tier-+-batch combination produces ≥1 generation. */
  blocked: boolean;
}

export function fitToBudget(input: CostInputs, available: number): FitToBudgetResult {
  const tierWalk: OutputSizeTier[] =
    input.sizeTier === 'standard'
      ? ['standard']
      : input.sizeTier === 'large'
        ? ['large', 'standard']
        : ['xl', 'large', 'standard'];

  const minRequired = 1;
  for (const tier of tierWalk) {
    const perUnit = getPerUnitCost({ ...input, sizeTier: tier });
    const affordable = Math.floor(available / perUnit);
    if (affordable >= minRequired) {
      const newBatch = Math.min(affordable, input.batchSize);
      return { sizeTier: tier, batchSize: newBatch, blocked: false };
    }
  }
  return { sizeTier: 'standard', batchSize: 1, blocked: true };
}
